import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, Users, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { useAuth } from '@/context/AuthContext';
import AuthLayout from '@/components/auth/AuthLayout';
import { markAccountSetupDone } from '@/components/auth/RequireAccountSetup';
import { completeSetup } from '@/lib/profileApi';

const EXPECTED_FILINGS = [
  { value: '1', label: 'Just 1' },
  { value: '2_5', label: '2 to 5' },
  { value: '6_20', label: '6 to 20' },
  { value: '21_plus', label: 'More than 20' },
];

const REQUIRED = [
  ['company_name', 'Business name'],
  ['billing_address_line1', 'Address line 1'],
  ['billing_city', 'City'],
  ['billing_postcode', 'Postcode'],
];

const Field = ({ id, label, optional, hint, children }) => (
  <div>
    <Label htmlFor={id}>
      {label}{optional && <span className="text-muted-foreground font-normal"> (optional)</span>}
    </Label>
    <div className="mt-2">{children}</div>
    {hint && <p className="text-xs text-muted-foreground mt-1">{hint}</p>}
  </div>
);

const Onboarding = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    business_type: 'company',
    company_name: '',
    phone: '',
    billing_address_line1: '',
    billing_address_line2: '',
    billing_city: '',
    billing_county: '',
    billing_postcode: '',
    billing_country: 'GB',
    billing_email: '',
    vat_number: '',
    default_po_number: '',
    expected_filings: '',
    invoice_mode: 'per_job',
  });

  const setField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));
  // A firm usually bills several clients at once, so offer one invoice per order by default.
  const setBusinessType = (value) => setForm((prev) => ({
    ...prev,
    business_type: value,
    invoice_mode: value === 'firm' ? 'consolidated' : 'per_job',
  }));

  const handleSubmit = async (event) => {
    event.preventDefault();
    const missing = REQUIRED.find(([key]) => !form[key].trim());
    if (missing) {
      toast.error(`${missing[1]} is required`);
      return;
    }
    // Blank optional answers are left out so they are not saved as empty text.
    const body = Object.fromEntries(Object.entries(form).filter(([, value]) => value.trim() !== ''));
    setSaving(true);
    try {
      await completeSetup(body);
      markAccountSetupDone(user?.id);
      navigate('/', { replace: true });
    } catch (err) {
      toast.error(err.message || 'Could not save your details. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const displayName = user?.user_metadata?.full_name || user?.email || 'there';
  const isFirm = form.business_type === 'firm';

  return (
    <AuthLayout>
      <form onSubmit={handleSubmit} className="w-full max-w-xl py-6" data-testid="account-setup-form">
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2">Welcome, {displayName}</h1>
          <p className="text-muted-foreground">A few details so your invoices and orders are right from the start. You can change any of this later in Settings.</p>
        </div>

        <div className="bg-card border border-border p-8 space-y-8">
          <section className="space-y-4">
            <Label className="text-base font-semibold">Who is placing orders?</Label>
            <RadioGroup value={form.business_type} onValueChange={setBusinessType} className="space-y-3">
              <label className="flex items-center gap-3 p-4 border border-border cursor-pointer" htmlFor="type-company">
                <RadioGroupItem value="company" id="type-company" />
                <Building2 className="w-5 h-5" />
                <span>
                  <span className="block font-medium">A company</span>
                  <span className="block text-xs text-muted-foreground">We file our own accounts</span>
                </span>
              </label>
              <label className="flex items-center gap-3 p-4 border border-border cursor-pointer" htmlFor="type-firm">
                <RadioGroupItem value="firm" id="type-firm" />
                <Users className="w-5 h-5" />
                <span>
                  <span className="block font-medium">An accounting firm</span>
                  <span className="block text-xs text-muted-foreground">We file accounts for our clients</span>
                </span>
              </label>
            </RadioGroup>
            <Field id="company_name" label={isFirm ? 'Firm name' : 'Company name'} hint="Shown as the billed party on your invoices.">
              <Input id="company_name" value={form.company_name} onChange={(e) => setField('company_name', e.target.value)} autoComplete="organization" />
            </Field>
            <Field id="phone" label="Phone" optional>
              <Input id="phone" value={form.phone} onChange={(e) => setField('phone', e.target.value)} autoComplete="tel" />
            </Field>
          </section>

          <section className="space-y-4">
            <Label className="text-base font-semibold">Billing address</Label>
            <Field id="billing_address_line1" label="Address line 1">
              <Input id="billing_address_line1" value={form.billing_address_line1} onChange={(e) => setField('billing_address_line1', e.target.value)} autoComplete="address-line1" />
            </Field>
            <Field id="billing_address_line2" label="Address line 2" optional>
              <Input id="billing_address_line2" value={form.billing_address_line2} onChange={(e) => setField('billing_address_line2', e.target.value)} autoComplete="address-line2" />
            </Field>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field id="billing_city" label="City">
                <Input id="billing_city" value={form.billing_city} onChange={(e) => setField('billing_city', e.target.value)} autoComplete="address-level2" />
              </Field>
              <Field id="billing_county" label="County" optional>
                <Input id="billing_county" value={form.billing_county} onChange={(e) => setField('billing_county', e.target.value)} />
              </Field>
              <Field id="billing_postcode" label="Postcode">
                <Input id="billing_postcode" value={form.billing_postcode} onChange={(e) => setField('billing_postcode', e.target.value)} autoComplete="postal-code" />
              </Field>
              <Field id="billing_country" label="Country">
                <Input id="billing_country" value={form.billing_country} onChange={(e) => setField('billing_country', e.target.value)} />
              </Field>
            </div>
          </section>

          <section className="space-y-4">
            <Label className="text-base font-semibold">Invoices</Label>
            <Field id="billing_email" label="Send invoices to" optional hint="Leave blank to use your sign-in email.">
              <Input id="billing_email" type="email" value={form.billing_email} onChange={(e) => setField('billing_email', e.target.value)} />
            </Field>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field id="vat_number" label="VAT number" optional hint="Printed on your invoices.">
                <Input id="vat_number" value={form.vat_number} onChange={(e) => setField('vat_number', e.target.value)} />
              </Field>
              <Field id="default_po_number" label="PO reference" optional hint="Added to every order. Changeable per order.">
                <Input id="default_po_number" value={form.default_po_number} onChange={(e) => setField('default_po_number', e.target.value)} />
              </Field>
            </div>
            <RadioGroup value={form.invoice_mode} onValueChange={(value) => setField('invoice_mode', value)} className="space-y-3">
              <label className="flex items-start gap-3 p-4 border border-border cursor-pointer" htmlFor="mode-per-job">
                <RadioGroupItem value="per_job" id="mode-per-job" className="mt-0.5" />
                <span>
                  <span className="block text-sm font-medium">One invoice per company</span>
                  <span className="block text-xs text-muted-foreground">Each company in an order gets its own invoice.</span>
                </span>
              </label>
              <label className="flex items-start gap-3 p-4 border border-border cursor-pointer" htmlFor="mode-consolidated">
                <RadioGroupItem value="consolidated" id="mode-consolidated" className="mt-0.5" />
                <span>
                  <span className="block text-sm font-medium">One invoice per order</span>
                  <span className="block text-xs text-muted-foreground">One invoice listing every company in the order, with its Job ID and services. UK orders only.</span>
                </span>
              </label>
            </RadioGroup>
          </section>

          <section className="space-y-4">
            <Field id="expected_filings" label="How many companies do you expect to file each year?" optional>
              <select
                id="expected_filings"
                value={form.expected_filings}
                onChange={(e) => setField('expected_filings', e.target.value)}
                className="w-full h-10 border border-input bg-background px-3 text-sm"
                data-testid="expected-filings"
              >
                <option value="">Prefer not to say</option>
                {EXPECTED_FILINGS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
              </select>
            </Field>
          </section>

          <Button type="submit" className="w-full" disabled={saving} data-testid="account-setup-submit">
            {saving ? 'Saving…' : 'Get started'}
            {!saving && <ArrowRight className="w-4 h-4 ml-2" />}
          </Button>
        </div>
      </form>
    </AuthLayout>
  );
};

export default Onboarding;
